<script setup lang="ts">
import { computedAsync } from "@vueuse/core";
import { $getNodeByKey, $isTextNode } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed } from "vue";

import { getPropertyTypeaheadOption } from "~/common/properties";
import Avatar from "~/components/dumb/Avatar.vue";
import EntityPreviewTooltip from "~/components/dumb/EntityPreviewTooltip.vue";
import UserLink from "~/components/dumb/UserLink.vue";
import { DEACTIVATED_USER_LABEL } from "~/components/visualization/constants";
import { USER_PROPERTY_KINDS } from "~/constants/property";
import { DEFAULT_CHIP_COLOR } from "~/constants/style";
import { XIcon } from "~/icons";
import type { TypeaheadOption } from "~/shared/types";
import { useDataStore, usePageStore } from "~/stores";
import { getChipColorsHex } from "~/utils/color";
import { getUserDisplayName } from "~/utils/user";

import { $isTypeaheadNode } from "./TypeaheadNode";

type Value = TypeaheadOption["value"];

const props = defineProps<{
  nodeKey: string;
  propertyDuid: string;
  value: Value;
}>();

const pageStore = usePageStore();
const dataStore = useDataStore();
const editor = useLexicalComposer();

const isRemovable = editor._config.namespace.startsWith("title");

const propertyAndOption = computedAsync(() => getPropertyTypeaheadOption(props.propertyDuid, props.value), null);
const property = computed(() => propertyAndOption.value?.[0]);
const option = computed(() => propertyAndOption.value?.[1]);
const user = computed(() => {
  if (!property.value || !USER_PROPERTY_KINDS.has(property.value.kind) || typeof props.value !== "string") {
    return undefined;
  }
  return dataStore.getUserByDuid(props.value);
});
// TODO make more robust and use a proper tooltip
const label = computed(() => {
  if (!property.value) {
    return undefined;
  }
  if (option.value) {
    return option.value.label;
  }
  if (user.value) {
    return getUserDisplayName(user.value);
  }
  if (USER_PROPERTY_KINDS.has(property.value.kind)) {
    return DEACTIVATED_USER_LABEL;
  }
  return `(Unknown ${property.value.title.toLowerCase()})`;
});
const title = computed(() => `${property.value?.title}: ${label.value}`);

const colorSet = computed(() =>
  getChipColorsHex(option.value?.chipColorHex ?? user.value?.colorHex ?? DEFAULT_CHIP_COLOR, pageStore.theme)
);

const removeSelf = () => {
  editor.update(() => {
    const self = $getNodeByKey(props.nodeKey);
    if (!self) {
      return;
    }
    const prev = self.getPreviousSibling();
    const next = self.getNextSibling();
    if (
      prev &&
      next &&
      ($isTextNode(prev) || $isTypeaheadNode(prev)) &&
      ($isTextNode(next) || $isTypeaheadNode(next))
    ) {
      prev.setTextContent(`${prev.getTextContent().trimEnd()} `);
      next.setTextContent(`${next.getTextContent().trimStart()}`);
    }
    self.remove();
  });
};
</script>

<template>
  <div :title="title" class="group/entity relative -mt-0.5 inline-flex items-center align-middle">
    <EntityPreviewTooltip v-if="user" :user-duid="user.duid">
      <UserLink :user="user" no-underline>
        <div
          class="flex items-center justify-center rounded-sm border px-1 py-px text-xs font-normal hover:brightness-95 dark:hover:brightness-110"
          :style="{ color: colorSet.fore, backgroundColor: colorSet.back, borderColor: colorSet.border }">
          <Avatar :user="user" :is-contrast="pageStore.theme === 'light'" class="mr-1 icon-sm" />
          <div class="mr-0.5">
            {{ label }}
          </div>
        </div>
      </UserLink>
    </EntityPreviewTooltip>
    <div
      v-else
      class="flex items-center justify-center rounded-sm border px-1 py-px text-xs font-normal"
      :style="{ color: colorSet.fore, backgroundColor: colorSet.back, borderColor: colorSet.border }">
      <div v-if="option?.chipIcon" class="mr-1 icon-sm">
        <component :is="option.chipIcon" v-bind="{ ...option.chipIconArgs, class: 'icon-sm' }" />
      </div>
      <div :class="option?.chipIcon && 'mr-0.5'">
        {{ label }}
      </div>
    </div>

    <template v-if="isRemovable">
      <!-- dummy elements to help avoid overshooting the x -->
      <div class="absolute -top-1.5 right-0 hidden h-1.5 w-3 group-hover/entity:flex" />
      <div class="absolute top-0 -right-1.5 hidden h-3 w-1.5 group-hover/entity:flex" />
      <button
        type="button"
        class="absolute -top-1.5 -right-1.5 z-10 hidden icon-xs cursor-pointer items-center justify-center rounded-full border border-md bg-lt group-hover/entity:flex"
        aria-label="Remove"
        @click="removeSelf"
        @keydown.enter="removeSelf">
        <XIcon class="size-full text-lt" />
      </button>
    </template>
  </div>
</template>
